/**
 * AdminCronStatusPage — Phase 4d.
 *
 * Monitoring-Übersicht für alle 14 Background-Cron-Jobs:
 * - Status (ok / error / never_ran)
 * - Letzter Lauf, letzter Erfolg, letzter Fehler
 * - Run-Count / Error-Count
 * - Nächster geplanter Lauf
 * - Healthy-Indikator (unhealthy wenn >3x interval kein Lauf oder Status=error)
 *
 * Auto-Refresh alle 30 Sekunden.
 *
 * Route: /admin/cron-status (admin only)
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, Check, AlertTriangle, Clock, ArrowLeft, RefreshCw } from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";

const fmtAge = (s) => {
    if (s == null) return "—";
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)} min`;
    if (s < 86400) return `${Math.floor(s / 3600)} h`;
    return `${Math.floor(s / 86400)} d`;
};

const fmtInterval = (s) => {
    if (!s) return "—";
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)} min`;
    if (s < 86400) return `${Math.floor(s / 3600)} h`;
    return `${Math.floor(s / 86400)} Tage`;
};

const fmtMs = (ms) => {
    if (ms == null) return "—";
    if (ms < 1000) return `${Math.round(ms)} ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
    return `${Math.floor(ms / 60000)} min`;
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "medium" }); }
    catch { return iso; }
};

export default function AdminCronStatusPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastFetched, setLastFetched] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/cron-status");
            setData(r.data);
            setLastFetched(new Date());
        } catch (_e) {
            setData({ items: [], summary: { total: 0, healthy: 0, errors: 0, never_ran: 0 } });
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "admin") navigate("/");
        if (user) {
            load();
            const iv = setInterval(load, 30000);
            return () => clearInterval(iv);
        }
    }, [user, navigate, load]);

    if (!user) return <div className="p-8">Lade…</div>;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="cron-status-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/admin" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-admin">
                        <ArrowLeft className="w-3 h-3" /> Admin-Dashboard
                    </Link>
                    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <Activity className="w-7 h-7 text-[#FFCC00]" />
                            <div>
                                <h1 className="font-display font-black text-4xl md:text-5xl">Cron-Status</h1>
                                <p className="text-sm text-gray-300 mt-1">Background-Jobs in Echtzeit überwachen</p>
                            </div>
                        </div>
                        <Button onClick={load} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 rounded-sm" data-testid="refresh-btn">
                            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Neu laden
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-4">
                {data?.summary && (
                    <div className="grid grid-cols-4 gap-3" data-testid="cron-summary">
                        <SummaryCard label="Jobs gesamt" value={data.summary.total} testid="sum-total" />
                        <SummaryCard label="Healthy" value={data.summary.healthy} testid="sum-healthy" tone="success" />
                        <SummaryCard label="Mit Fehlern" value={data.summary.errors} testid="sum-errors" tone={data.summary.errors > 0 ? "error" : "muted"} />
                        <SummaryCard label="Nie gelaufen" value={data.summary.never_ran} testid="sum-never" tone={data.summary.never_ran > 0 ? "warn" : "muted"} />
                    </div>
                )}

                {lastFetched && (
                    <div className="text-[10px] text-gray-500 text-right">
                        Zuletzt geladen: {lastFetched.toLocaleTimeString("de-DE")} · Auto-Refresh alle 30s
                    </div>
                )}

                {data?.items && data.items.some((it) => it.perf?.samples > 0) && (
                    <SlowJobsPanel items={data.items} />
                )}

                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="cron-list">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-left px-3 py-2">Job</th>
                                <th className="text-right px-3 py-2">Intervall</th>
                                <th className="text-right px-3 py-2">Letzter Lauf</th>
                                <th className="text-right px-3 py-2">vor</th>
                                <th className="text-right px-3 py-2">Runs / Errors</th>
                                <th className="text-right px-3 py-2" title="Letzte Iteration · Ø der letzten 100 · 95-Perzentil">Dauer (letzte · Ø · p95)</th>
                                <th className="text-left px-3 py-2">Letzter Fehler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.items?.map((it) => (
                                <tr key={it.name} className={`border-t border-gray-100 ${!it.healthy ? "bg-red-50/40" : ""}`} data-testid={`cron-row-${it.name}`}>
                                    <td className="px-3 py-2">
                                        <StatusBadge healthy={it.healthy} status={it.status} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-bold text-[13px]">{it.label}</div>
                                        <div className="text-[10px] text-gray-500 font-mono-num">{it.name}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs font-mono-num text-gray-600">{fmtInterval(it.interval_s)}</td>
                                    <td className="px-3 py-2 text-right text-[11px] font-mono-num text-gray-600 whitespace-nowrap">{fmtDate(it.last_run_at)}</td>
                                    <td className={`px-3 py-2 text-right text-xs font-mono-num font-bold ${it.last_run_age_s != null && it.interval_s && it.last_run_age_s > it.interval_s * 2 ? "text-red-700" : "text-gray-700"}`}>
                                        {fmtAge(it.last_run_age_s)}
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs font-mono-num">
                                        <span className="font-bold">{it.run_count}</span>
                                        {it.error_count > 0 && <span className="text-red-700 ml-1">· {it.error_count}E</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right text-[11px] font-mono-num" data-testid={`cron-perf-${it.name}`}>
                                        {it.perf && it.perf.samples > 0 ? (
                                            <>
                                                <span className={`font-bold ${(it.perf.last_duration_ms || 0) > 5000 ? "text-amber-700" : "text-gray-800"}`}>
                                                    {fmtMs(it.perf.last_duration_ms)}
                                                </span>
                                                <span className="text-gray-500 mx-1">·</span>
                                                <span className="text-gray-700">{fmtMs(it.perf.avg_ms)}</span>
                                                <span className="text-gray-500 mx-1">·</span>
                                                <span className={`${(it.perf.p95_ms || 0) > 10000 ? "text-red-700 font-bold" : "text-gray-600"}`}>{fmtMs(it.perf.p95_ms)}</span>
                                                <div className="text-[9px] text-gray-400">{it.perf.samples} samples</div>
                                            </>
                                        ) : (
                                            <span className="text-gray-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-[10px] text-red-700 max-w-xs truncate" title={it.last_error || ""}>
                                        {it.last_error ? (
                                            <>
                                                <span className="font-bold">{fmtDate(it.last_error_at)}:</span> {it.last_error}
                                                {it.consecutive_errors > 1 && (
                                                    <span className="ml-1 text-red-900 font-bold">· {it.consecutive_errors}x in Folge</span>
                                                )}
                                                {it.alert_sent_at && (
                                                    <div className="text-[9px] text-amber-700 font-bold uppercase tracking-wider mt-0.5" data-testid={`cron-alert-${it.name}`}>
                                                        📧 Alert gesendet: {fmtDate(it.alert_sent_at)}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-gray-300">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {(!data?.items?.length && !loading) && (
                                <tr>
                                    <td colSpan={8} className="px-3 py-8 text-center text-gray-500" data-testid="cron-empty">
                                        Noch keine Cron-Status-Daten verfügbar (Jobs laufen, aber haben noch keinen Status geschrieben)
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-sm p-3 text-xs text-blue-900">
                    <strong>Healthy-Kriterien:</strong> Letzter Lauf erfolgreich UND innerhalb &lt;3x Intervall.
                    Rote Zeile = Unhealthy: entweder letzter Lauf fehlerhaft oder Job hängt (kein Lauf seit &gt;3x Intervall).
                    <br />
                    <strong>Auto-Alert:</strong> Bei <strong>2x Fehler in Folge</strong> wird automatisch eine E-Mail an <code>CRON_ALERT_EMAIL</code> (Fallback: admin@twowheels.info) versendet (Cooldown 6h). Bei Recovery folgt eine Wiederherstellungs-Mail.
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ healthy, status }) {
    if (status === "never_ran") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-gray-200 text-gray-600">
                <Clock className="w-3 h-3" /> Wartend
            </span>
        );
    }
    if (!healthy) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800">
                <AlertTriangle className="w-3 h-3" /> Unhealthy
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-900">
            <Check className="w-3 h-3" /> OK
        </span>
    );
}

function SummaryCard({ label, value, tone = "default", testid }) {
    const tones = {
        default: "bg-white border-gray-200",
        success: "bg-green-50 border-green-200",
        error: "bg-red-50 border-red-200",
        warn: "bg-amber-50 border-amber-200",
        muted: "bg-gray-50 border-gray-200 text-gray-500",
    };
    return (
        <div className={`border rounded-sm p-4 ${tones[tone]}`} data-testid={testid}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">{label}</div>
            <div className="font-display font-black text-2xl text-[#111827] font-mono-num leading-none">{value}</div>
        </div>
    );
}

function SlowJobsPanel({ items }) {
    const slow = [...items]
        .filter((it) => it.perf?.samples > 0 && it.perf.avg_ms != null)
        .sort((a, b) => (b.perf.avg_ms || 0) - (a.perf.avg_ms || 0))
        .slice(0, 3);
    if (slow.length === 0) return null;
    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="slow-jobs-panel">
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-900">⏱ Performance · Top 3 langsamste Jobs</span>
                <span className="text-[10px] text-amber-700">(Ø der letzten 100 Iterationen)</span>
            </div>
            <ul className="divide-y divide-gray-100">
                {slow.map((it, idx) => (
                    <li key={it.name} className="px-4 py-2 flex items-center justify-between" data-testid={`slow-job-${idx}`}>
                        <div className="flex items-center gap-3">
                            <span className="text-2xl font-black font-mono-num text-gray-300">#{idx + 1}</span>
                            <div>
                                <div className="font-bold text-sm">{it.label}</div>
                                <div className="text-[10px] text-gray-500 font-mono-num">{it.name} · alle {fmtInterval(it.interval_s)}</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="font-mono-num font-black text-xl text-amber-900">{fmtMs(it.perf.avg_ms)}</div>
                            <div className="text-[10px] text-gray-500 font-mono-num">p95: {fmtMs(it.perf.p95_ms)} · max: {fmtMs(it.perf.max_ms)} · n={it.perf.samples}</div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
